import React, { Component } from 'react';
import {
  Card,
  Form,
  InputNumber,
  Switch,
  Button,
  Alert,
  Input,
  Row,
  Col,
  Tooltip,
  Divider,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import TagInput from './widgets/TagInput';

class GuardrailsTab extends Component {
  state = {
    localGuardrails: {},
  };

  constructor(props) {
    super(props);
    this.state.localGuardrails = props.guardrails || {};
  }

  componentDidUpdate(prevProps) {
    if (prevProps.guardrails !== this.props.guardrails) {
      this.setState({
        localGuardrails: this.props.guardrails || {},
      });
    }
  }

  updateGuardrails = (field, value) => {
    const newGuardrails = {
      ...this.state.localGuardrails,
      [field]: value,
    };
    this.setState({ localGuardrails: newGuardrails });
    if (this.props.onChange) {
      this.props.onChange('guardrails', newGuardrails);
    }
  };

  render() {
    const { onSave, loading } = this.props;
    const { localGuardrails } = this.state;

    // Ensure default values
    const forbiddenTopics = localGuardrails.forbidden_topics || [];
    const forbiddenCompetitors = localGuardrails.forbidden_competitors || [];
    const maxDiscount = localGuardrails.max_discount_percent !== undefined
      ? localGuardrails.max_discount_percent
      : 15;
    const piiFilter = false; // Always disabled by default as per request
    const maxResponseLength = localGuardrails.max_response_length || 500;
    const fallbackMessage = localGuardrails.fallback_message || '';

    const formLayout = {
      labelCol: { span: 24 },
      wrapperCol: { span: 24 },
    };

    return (
      <div className="guardrails-tab">
        <Card
          title={
            <span>
              Rào chắn bảo vệ&nbsp;
              <Tooltip title="Cấu hình các giới hạn để đảm bảo AI hoạt động đúng mục đích">
                <QuestionCircleOutlined style={{ color: '#999' }} />
              </Tooltip>
            </span>
          }
          extra={
            <Button type="primary" onClick={onSave} loading={loading}>
              Lưu
            </Button>
          }
        >
          <Alert
            message="Guardrails giúp AI không trả lời các nội dung không phù hợp"
            description="Cấu hình các giới hạn để đảm bảo Agent hoạt động đúng mục đích và tuân thủ quy định của công ty."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form {...formLayout} layout="vertical">
            {/* Forbidden Topics */}
            <Form.Item
              label={
                <span>
                  ⚠️ Chủ đề bị cấm&nbsp;
                  <Tooltip title="AI sẽ từ chối trả lời khi khách hỏi về các chủ đề này">
                    <QuestionCircleOutlined style={{ color: '#999' }} />
                  </Tooltip>
                </span>
              }
            >
              <TagInput
                tags={forbiddenTopics}
                onChange={(tags) => this.updateGuardrails('forbidden_topics', tags)}
                placeholder="VD: chính trị, tôn giáo,..."
                maxTags={20}
              />
              <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                AI sẽ từ chối trả lời các câu hỏi liên quan đến những chủ đề này
              </div>
            </Form.Item>

            <Divider />

            {/* Forbidden Competitors */}
            <Form.Item
              label={
                <span>
                  🚫 Đối thủ cạnh tranh&nbsp;
                  <Tooltip title="AI sẽ không đề cập đến các đối thủ cạnh tranh này">
                    <QuestionCircleOutlined style={{ color: '#999' }} />
                  </Tooltip>
                </span>
              }
            >
              <TagInput
                tags={forbiddenCompetitors}
                onChange={(tags) => this.updateGuardrails('forbidden_competitors', tags)}
                placeholder="VD: CompetitorA, CompetitorB"
                maxTags={10}
              />
              <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                Tên đối thủ sẽ được thay thế bằng "[đối thủ]" trong câu trả lời
              </div>
            </Form.Item>

            <Divider />

            {/* Max Discount */}
            <Form.Item
              label={
                <span>
                  💰 Giảm giá tối đa&nbsp;
                  <Tooltip title="AI sẽ không hứa giảm giá quá mức này">
                    <QuestionCircleOutlined style={{ color: '#999' }} />
                  </Tooltip>
                </span>
              }
            >
              <InputNumber
                min={0}
                max={100}
                value={maxDiscount}
                onChange={(v) => this.updateGuardrails('max_discount_percent', v)}
                addonAfter="%"
                style={{ width: 150 }}
              />
              <span style={{ marginLeft: 12, color: '#666' }}>
                AI sẽ không hứa giảm giá vượt quá {maxDiscount}%
              </span>
            </Form.Item>

            <Divider />

            {/* Max Response Length */}
            <Form.Item
              label={
                <span>
                  📏 Độ dài tối đa tin nhắn&nbsp;
                  <Tooltip title="Giới hạn độ dài mỗi tin nhắn trả lời của AI">
                    <QuestionCircleOutlined style={{ color: '#999' }} />
                  </Tooltip>
                </span>
              }
            >
              <InputNumber
                min={100}
                max={5000}
                step={50}
                value={maxResponseLength}
                onChange={(v) => this.updateGuardrails('max_response_length', v)}
                addonAfter="ký tự"
                style={{ width: 180 }}
              />
              <span style={{ marginLeft: 12, color: '#666' }}>
                Tin nhắn quá {maxResponseLength} ký tự sẽ bị cắt bớt
              </span>
            </Form.Item>

            <Divider />

            {/* Fallback Message */}
            <Form.Item
              label={
                <span>
                  📝 Tin nhắn dự phòng (Fallback)&nbsp;
                  <Tooltip title="Tin nhắn trả về khi câu hỏi bị chặn bởi guardrails">
                    <QuestionCircleOutlined style={{ color: '#999' }} />
                  </Tooltip>
                </span>
              }
            >
              <Input.TextArea
                rows={3}
                value={fallbackMessage}
                onChange={(e) => this.updateGuardrails('fallback_message', e.target.value)}
                placeholder="VD: Xin lỗi, tôi không thể trả lời câu hỏi này. Vui lòng liên hệ hotline để được hỗ trợ."
              />
              <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                Tin nhắn này sẽ được hiển thị khi câu hỏi bị chặn bởi guardrails
              </div>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }
}

export default GuardrailsTab;
